import {useIssueCreateMetadata} from '@github-ui/issue-create/IssueCreateDataContext'
import {useMemo} from 'react'

import type {DraftIssue} from '../content-preview-types'
import {useDraftIssueFormSync} from './use-draft-issue-form-sync'
import {useIssueTypesQuery} from './use-issue-types-query'
import {useOnChangeCallback} from './use-on-change-callback'

function isEqual(left: string | undefined, right: string | undefined) {
  return left?.toLowerCase() === right?.toLowerCase()
}

export function useDraftIssueType(draftIssue: DraftIssue) {
  const {
    repository: formRepository,
    issueType: formIssueType,
    setIssueType: setFormIssueType,
  } = useIssueCreateMetadata()
  const onChange = useOnChangeCallback(draftIssue)

  const resolverQuery = useIssueTypesQuery({
    owner: formRepository?.owner.login,
    repo: formRepository?.name,
  })
  const resolvedIssueType = useMemo(() => {
    if (resolverQuery.isLoading) return null
    if (!draftIssue.issueType) return null
    return resolverQuery.data?.find(t => isEqual(t.name, draftIssue.issueType)) || null
  }, [draftIssue.issueType, resolverQuery.data, resolverQuery.isLoading])

  return useDraftIssueFormSync(draftIssue, {
    draftValue: draftIssue.issueType,
    formValue: formIssueType,
    setFormValue: setFormIssueType,
    onChange: (newValue, skipVersionBump) => onChange({issueType: newValue?.name}, skipVersionBump),
    resolverResult: {
      isLoading: resolverQuery.isLoading,
      data: resolvedIssueType,
    },
    hasPermission: repo => repo?.viewerIssueCreationPermissions?.typeable,
    isEqual: (a, b) => isEqual(a?.name, b?.name),
    defaultValue: null,
  })
}
